/**
 * Decorative market image with built-in URL gating and error fallback.
 *
 * Centralises the things every image slot in the widget needs:
 *   • Resolve the URL up-front via `resolveMarketImageUrl`: only safe
 *     http(s) URLs survive, and Kalshi's own generic fallback artwork
 *     (4 diamonds, calendar, temperature, match tie, ...) is treated as
 *     "no image" so it never leaks into the widget (APPS-5374).
 *   • Reset the error flag when `src` changes so a previous load's failure
 *     doesn't pin the placeholder when a new (potentially loadable) image
 *     arrives in the same mounted instance.
 *   • Fall back to the branded `<ImageFallback />` tile when the URL is
 *     missing / unsafe / Kalshi-fallback or the network load errors.
 *
 * Shape is dictated by the caller's container — pass `className` for sizing
 * and `object-fit`; the rounded mask lives on the parent slot.
 */

import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { resolveMarketImageUrl } from "../utils/market-image";
import { ImageFallback } from "./image-fallback";

export interface MarketImageProps {
  src?: string | null;
  className?: string;
}

export function MarketImage({ src, className }: MarketImageProps): JSX.Element {
  const [hasError, setHasError] = useState(false);
  const resolved = resolveMarketImageUrl(src);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (!resolved || hasError) return <ImageFallback />;

  return (
    <img
      alt=""
      className={className}
      loading="lazy"
      onError={() => setHasError(true)}
      src={resolved}
    />
  );
}
